@use '../../styles/colors';

$pill-background-color-variable-name: --ixf-pill-background;

.pill, .pillSkeleton {
  --ixf-pill-opacity: 0.2;
  --ixf-pill-border-radius: var(--ixf-border-radius-1);
  --ixf-pill-height: 20px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ixf-pill-border-radius);
  height: var(--ixf-pill-height);
  position: relative;

  &.fillParent {
    width: 100%;
  }

  &.size {
    &-small {
      --ixf-pill-padding: 0 var(--ixf-space-1);
      --ixf-pill-height: 16px;
    }

    &-large {
      --ixf-pill-padding: 6px var(--ixf-space-2);
      --ixf-pill-border-radius: 6px;
      --ixf-pill-height: 32px;

      &.hasAffixIcon {
        --ixf-pill-padding: 6px 6px 6px var(--ixf-space-2);
      }
    }
  }
}

.pillSkeleton {
  --ixf-loading-skeleton-border-radius-override: var(--ixf-pill-border-radius);
  --ixf-loading-skeleton-line-height: var(--ixf-pill-height);

  height: var(--ixf-pill-height);
}

.pill {
  // Variant default: primary
  --ixf-pill-font-color: var(--ixf-color-primary-dk);
  --ixf-pill-font-family: var(--ixf-font-family-main);
  --ixf-pill-padding: 0 6px;

  width: fit-content;
  color: var(--ixf-pill-font-color);
  font-family: var(--ixf-pill-font-family);
  padding: var(--ixf-pill-padding);
  background: var(--ixf-color-background);

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: var(--ixf-pill-border-radius);
    background-color: var(#{$pill-background-color-variable-name});
  }

  @include colors.define-best-supported-color-value(
    $pill-background-color-variable-name,
    color(from var(--ixf-color-primary-md) srgb r g b / var(--ixf-pill-opacity)),
    color(from var(--ixf-color-primary-md) display-p3 r g b / var(--ixf-pill-opacity)),
  );

  > * {
    z-index: 1;
  }

  &.hasAffixIcon {
    --ixf-pill-padding: 0 var(--ixf-space-1) 0 6px;
  }

  &.variant {
    &-secondary {
      --ixf-pill-font-color: var(--ixf-color-secondary-dk);

      @include colors.define-best-supported-color-value(
        $pill-background-color-variable-name,
        color(from var(--ixf-color-secondary-md) srgb r g b / var(--ixf-pill-opacity)),
        color(from var(--ixf-color-secondary-md) display-p3 r g b / var(--ixf-pill-opacity)),
      );
    }

    &-tertiary {
      --ixf-pill-font-color: var(--ixf-color-tertiary-dk);

      @include colors.define-best-supported-color-value(
        $pill-background-color-variable-name,
        color(from var(--ixf-color-tertiary-md) srgb r g b / var(--ixf-pill-opacity)),
        color(from var(--ixf-color-tertiary-md) display-p3 r g b / var(--ixf-pill-opacity)),
      );
    }

    &-quaternary {
      --ixf-pill-font-color: var(--ixf-color-quaternary-dk);

      @include colors.define-best-supported-color-value(
        $pill-background-color-variable-name,
        color(from var(--ixf-color-quaternary-md) srgb r g b / var(--ixf-pill-opacity)),
        color(from var(--ixf-color-quaternary-md) display-p3 r g b / var(--ixf-pill-opacity)),
      );
    }

    &-danger {
      --ixf-pill-font-color: var(--ixf-color-danger-dk);

      @include colors.define-best-supported-color-value(
        $pill-background-color-variable-name,
        color(from var(--ixf-color-danger-md) srgb r g b / var(--ixf-pill-opacity)),
        color(from var(--ixf-color-danger-md) display-p3 r g b / var(--ixf-pill-opacity)),
      );
    }

    &-success {
      --ixf-pill-font-color: var(--ixf-color-success-dk);

      @include colors.define-best-supported-color-value(
        $pill-background-color-variable-name,
        color(from var(--ixf-color-success-md) srgb r g b / var(--ixf-pill-opacity)),
        color(from var(--ixf-color-success-md) display-p3 r g b / var(--ixf-pill-opacity)),
      );
    }

    &-green {
      --ixf-pill-font-color: var(--ixf-color-green-dk);

      @include colors.define-best-supported-color-value(
        $pill-background-color-variable-name,
        color(from var(--ixf-color-green-md) srgb r g b / var(--ixf-pill-opacity)),
        color(from var(--ixf-color-green-md) display-p3 r g b / var(--ixf-pill-opacity)),
      );
    }

    &-dark {
      #{$pill-background-color-variable-name}: var(--ixf-text-tone-md);
      --ixf-pill-font-color: var(--ixf-color-background);
    }

    &-neutral {
      #{$pill-background-color-variable-name}: var(--ixf-color-neutral-lt);
      --ixf-pill-font-color: var(--ixf-text-tone-md);
    }

    &-neutral-lt {
      #{$pill-background-color-variable-name}: var(--ixf-color-background);
      --ixf-pill-font-color: var(--ixf-text-tone-md);
    }
  }

  > * {
    color: var(--ixf-pill-font-color) !important;
    position: relative;
  }

  .affixIcon button {
    cursor: pointer;
  }

  .affixIcon, .affixIcon button, .affixIcon > svg, .affixIcon button > svg {
    height: 16px;
    width: 16px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .affixIcon > svg path {
    fill: var(--ixf-pill-font-color);
  }

  &.isInteractive:not(:disabled) {
    cursor: pointer;
  }
}
